import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InGoLogo from '../components/InGoLogo';
import InGoMap from '../components/maps/InGoMap';
import '../components/customer/CustomerApp.css';

import { clearCustomerSession, getCustomerSession } from '../lib/customerSession';
import { isApiError, refusalMessage } from '../api';
import { booking } from '../api/booking';
import { mapsApi } from '../api/maps';
import { nearbyApi } from '../api/nearby';
import { useLiveLocation } from '../hooks/useLiveLocation';
import { useNearbyRiders } from '../lib/useNearbyRiders';

function greetingFirstName(profile) {
  if (!profile || typeof profile !== 'object') return '';
  const full = String(profile.full_name || '').trim();
  if (full) {
    const first = full.split(/\s+/)[0];
    return first || full;
  }
  const email = String(profile.email || '').trim();
  if (email && email.includes('@')) {
    return email.split('@')[0] || '';
  }
  return '';
}

function IconChevron() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M12 3a4.5 4.5 0 0 0-4.5 4.5V10l-1.2 2.4A1 1 0 0 1 7.2 14h9.6a1 1 0 0 1 .9-1.6L17 10V7.5A4.5 4.5 0 0 0 12 3Z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M10.5 18a1.5 1.5 0 0 0 3 0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** A parcel: InGo delivers parcels, and has no shop. */
function IconDeliverBag() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden>
      <path d="M12 3 4 7v10l8 4 8-4V7l-8-4Z" fill="#EC6C23" fillOpacity="0.15" stroke="#EC6C23" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="m4 7 8 4 8-4M12 11v10" stroke="#EC6C23" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Deliveries still under way, from booked to handed over: the home's "Now". */
const UNDER_WAY = ['requested', 'offered', 'counter_bid', 'accepted', 'picked_up', 'delivered'];
const FINISHED = ['completed', 'cancelled'];
const HOME_LIMIT = 3;

function badgeFor(status) {
  if (status === 'cancelled') return 'ch-ro__badge ch-ro__badge--cancelled';
  if (status === 'completed' || status === 'delivered') return 'ch-ro__badge ch-ro__badge--delivered';
  if (['requested', 'offered', 'counter_bid'].includes(status)) return 'ch-ro__badge ch-ro__badge--finding';
  return 'ch-ro__badge ch-ro__badge--active';
}

/** "12 Market Street" from "12 Market Street, Central District": the place, to its first comma. */
function placeOf(pin) {
  return String(pin?.address || '').split(',')[0].trim() || '—';
}

function money(price) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: price?.currency || 'USD' }).format((price?.total_minor || 0) / 100);
}

function when(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function DeliveryRow({ delivery, onOpen }) {
  return (
    <li>
      <button type="button" className="ch-ro" onClick={() => onOpen(delivery)}>
        <span className="ch-ro__icon ch-ro__icon--orange" aria-hidden>
          <IconDeliverBag />
        </span>
        <span className="ch-ro__body">
          <span className="ch-ro__name">
            {placeOf(delivery.pickup)} → {placeOf(delivery.dropoff)}
          </span>
          <span className="ch-ro__date">
            {delivery.reference} · {when(delivery.requested_at)}
          </span>
          <span className={badgeFor(delivery.status)}>{delivery.status_label}</span>
        </span>
        <span className="ch-ro__price">{money(delivery.price)}</span>
      </button>
    </li>
  );
}

/**
 * The home of a client on InGo's API (ingo-e0, 2 Oct 2026): the city on a
 * map with the free riders near the client moving on it, then Deliver,
 * offered while InGo prices a delivery service (`quotes/services`), the
 * client's deliveries under way and the latest finished
 * (`customer/me/deliveries`), each opening InGo's tracking. There is no
 * shop: InGo delivers parcels only.
 *
 * The map (Rio, 2 Oct 2026: "on about 60% of the screen ... a map with the
 * near by vehicle real life like the one we have in kangaru"; his answers:
 * a floating header on the map, free riders only, InGo's own vehicles): the
 * top 60 %, the riders asked every five seconds while the screen is in view
 * (InGo ADR 0036), around the client's position, or the city's centre while
 * the phone has given none.
 */
function ApiCustomerHome({ api, nearby, maps }) {
  const navigate = useNavigate();
  const displayName = useMemo(() => greetingFirstName(getCustomerSession()), []);
  const [services, setServices] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [loadError, setLoadError] = useState('');
  const live = useLiveLocation({ mapThrottleMs: 4000 });
  const me = live.located && live.lat != null && live.lng != null ? { lat: live.lat, lng: live.lng } : null;
  const [centre, setCentre] = useState(null);
  const vehicles = useNearbyRiders(me ?? centre, { nearby });

  useEffect(() => {
    let cancelled = false;
    maps.mapConfig().then(
      (config) => !cancelled && config?.centre && setCentre({ lat: config.centre.latitude, lng: config.centre.longitude }),
      () => {},
    );

    return () => {
      cancelled = true;
    };
  }, [maps]);

  useEffect(() => {
    let cancelled = false;
    // Unknown is not "none": a failed read leaves Deliver offered, and the price screen says why.
    api.services().then(
      (list) => !cancelled && setServices(list),
      () => !cancelled && setServices(undefined),
    );
    api.deliveries().then(
      (list) => !cancelled && setDeliveries(list),
      (err) => {
        if (cancelled) return;
        if (isApiError(err) && err.status === 401) {
          clearCustomerSession();
          navigate('/login', { replace: true });
          return;
        }
        setDeliveries([]);
        setLoadError(refusalMessage(err));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [api, navigate]);

  const underWay = (deliveries || []).filter((d) => UNDER_WAY.includes(d.status)).slice(0, HOME_LIMIT);
  const finished = (deliveries || []).filter((d) => FINISHED.includes(d.status)).slice(0, HOME_LIMIT);
  const canDeliver = services !== null && (services === undefined || services.length > 0);
  const open = (delivery) => navigate(`/live-tracking?delivery=${encodeURIComponent(delivery.id)}`);

  return (
    <div className="ch-home ch-home--map" role="main" aria-label="InGo home">
      <section className="ch-map" aria-label="Riders near you">
        <InGoMap pins={[]} me={me} vehicles={vehicles} label="Riders near you" height="100%" bleed focusZoom={14} />
        <header className="ch-floatbar">
          <InGoLogo variant="nav" className="ch-floatbar__logo" />
          <button type="button" className="ch-premium-nav__btn" aria-label="Notifications" onClick={() => navigate('/notifications')}>
            <BellIcon />
          </button>
        </header>
      </section>

      <div className="ch-home__sheet">
        <h1 className="ch-home__hi">{displayName ? `Hi, ${displayName}` : 'Hi there'}</h1>
        <section className="ch-svc-cards ch-svc-cards--sheet" aria-label="Send a parcel">
          {canDeliver ? (
            <button type="button" className="ch-deliver" aria-label="Deliver" onClick={() => navigate('/request-delivery')}>
              <span className="ch-svc-card__icon ch-svc-card__icon--orange">
                <IconDeliverBag />
              </span>
              <span className="ch-deliver__text">
                <span className="ch-deliver__label">Deliver</span>
                <span className="ch-deliver__sub">Send a parcel</span>
              </span>
              <span className="ch-deliver__chev">
                <IconChevron />
              </span>
            </button>
          ) : null}
          {services !== null && services !== undefined && services.length === 0 ? (
            <p className="ch-recent__empty">Deliveries cannot be booked right now. Try again later.</p>
          ) : null}
        </section>

        {underWay.length > 0 ? (
          <section className="ch-recent" aria-label="Under way">
            <div className="ch-recent__head">
              <h2 className="ch-recent__title">Under way</h2>
            </div>
            <ul className="ch-recent__list">
              {underWay.map((d) => (
                <DeliveryRow key={d.id} delivery={d} onOpen={open} />
              ))}
            </ul>
          </section>
        ) : null}

        <section className="ch-recent" aria-label="Recent deliveries">
          <div className="ch-recent__head">
            <h2 className="ch-recent__title">Recent deliveries</h2>
            <button type="button" className="ch-recent__viewall" onClick={() => navigate('/orders')}>
              View all
            </button>
          </div>
          {deliveries === null ? (
            <p className="ch-recent__empty">Loading your deliveries…</p>
          ) : loadError ? (
            <p className="ch-recent__empty" role="alert">
              {loadError}
            </p>
          ) : finished.length === 0 ? (
            <p className="ch-recent__empty">{underWay.length > 0 ? 'Nothing finished yet.' : 'No deliveries yet. Tap Deliver to send your first parcel.'}</p>
          ) : (
            <ul className="ch-recent__list">
              {finished.map((d) => (
                <DeliveryRow key={d.id} delivery={d} onOpen={open} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

/** The client's home, on InGo's API only (the app is delivery only, 2 Oct 2026). */
export default function CustomerHomePage({ api = booking, nearby = nearbyApi, maps = mapsApi }) {
  return <ApiCustomerHome api={api} nearby={nearby} maps={maps} />;
}

